import React, { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { motion } from "framer-motion"
import {CheckCircle2, Download, Shield, Lock, FileText, AlertTriangle} from 'lucide-react'
import { lumi } from "./lib/lumi"

function DownloadPage() {
  const [searchParams] = useSearchParams()
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)
  const [leadName, setLeadName] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get('token')
      
      if (!token) {
        setTokenValid(false)
        setError("Token de acesso não fornecido")
        return
      }

      try {
        const result = await lumi.functions.invoke('validateToken', {
          method: 'POST',
          body: { token }
        })

        if (result?.valid) {
          setTokenValid(true)
          setLeadName(result.leadName || "")
          
          // Disparar evento Lead do Meta Pixel após validação
          if (typeof window !== "undefined" && (window as any).fbq) {
            (window as any).fbq("track", "Lead")
          }
        } else {
          setTokenValid(false)
          setError(result?.error || "Token inválido")
        }
      } catch (err: any) {
        setTokenValid(false)
        setError(err?.data?.error || "Erro ao validar token")
        console.error("Token validation error:", err)
      }
    }

    validateToken()
  }, [searchParams])

  const handleDownload = () => {
    // URL do ebook - Arquivo hospedado no Lumi
    const ebookUrl = "https://file.lumi.new/p393718903031656448/SPF2003548844634374144.pdf"
    
    // Criar elemento <a> para forçar download
    const link = document.createElement("a")
    link.href = ebookUrl
    link.download = "Golpes-Final-de-Ano-Guia-Anti-Golpe.pdf"
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Exibir erro se token inválido
  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-orange-50 flex items-center justify-center px-6">
        <div className="max-w-2xl mx-auto text-center">
          <AlertTriangle className="w-24 h-24 text-red-500 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Acesso Negado</h1>
          <p className="text-xl text-gray-700 mb-8">{error}</p>
          <a
            href="/finaldeano"
            className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 rounded-full transition-all duration-300"
          >
            Voltar para cadastro
          </a>
        </div>
      </div>
    )
  }

  // Exibir loading enquanto valida
  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-700">Validando acesso...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-sky-50">
      {/* Success Section */}
      <section className="pt-20 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <CheckCircle2 className="w-24 h-24 text-emerald-500 mx-auto mb-6" />
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {leadName && `Olá, ${leadName}! `}Cadastro realizado com <span className="text-emerald-600">sucesso!</span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-8">
              Seu material está pronto para download.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-12 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-emerald-200"
          >
            <div className="text-center mb-8">
              <FileText className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Baixe seu material agora
              </h2>
              <p className="text-gray-600">
                Clique no botão abaixo para fazer o download do seu ebook
              </p>
            </div>

            <motion.button
              onClick={handleDownload}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xl font-bold px-8 py-5 rounded-full shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
            >
              <Download className="w-6 h-6" />
              <span>Baixar Ebook Agora</span>
            </motion.button>

            <div className="mt-8 p-6 bg-sky-50 rounded-xl">
              <div className="flex items-start gap-3 mb-4">
                <Lock className="w-5 h-5 text-sky-600 flex-shrink-0 mt-1" />
                <p className="text-sm text-gray-700">
                  <strong>Seus dados estão seguros:</strong> Não compartilhamos suas informações com terceiros.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-sky-600 flex-shrink-0 mt-1" />
                <p className="text-sm text-gray-700">
                  <strong>Também enviamos por email:</strong> Verifique sua caixa de entrada (e spam).
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Next Steps Section */}
      <section className="py-12 px-6 bg-gradient-to-br from-sky-900 to-cyan-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Próximos passos
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="text-4xl mb-4">📖</div>
              <h3 className="text-xl font-bold text-white mb-2">1. Leia o material</h3>
              <p className="text-sky-100">
                Dedique alguns minutos para ler e absorver o conteúdo
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="text-xl font-bold text-white mb-2">2. Aplique o método P.A.R.E.</h3>
              <p className="text-sky-100">
                Use as técnicas antes de qualquer ação suspeita
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <div className="text-4xl mb-4">👥</div>
              <h3 className="text-xl font-bold text-white mb-2">3. Compartilhe</h3>
              <p className="text-sky-100">
                Proteja seus familiares e amigos compartilhando o conhecimento
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Upsell Section */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 md:p-12 border-2 border-orange-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              Quer proteção completa o ano todo?
            </h2>
            <p className="text-lg text-gray-700 text-center mb-8">
              No <strong>Guia Anti-Golpe Digital Completo</strong>, você aprende a se proteger contra todos os tipos de golpes, não apenas os de final de ano.
            </p>
            
            <div className="space-y-3 mb-8">
              {[
                "Método P.A.R.E. completo e detalhado",
                "Golpes de WhatsApp, Pix, Instagram e mais",
                "Checklist para qualquer situação suspeita",
                "Como proteger toda a família",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                  <span className="text-gray-800">{item}</span>
                </div>
              ))}
            </div>

            <a
              href="https://pay.kiwify.com.br/FFX6AT1"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window !== "undefined" && (window as any).fbq) {
                  (window as any).fbq("track", "InitiateCheckout")
                }
              }}
              className="block w-full bg-orange-600 hover:bg-orange-700 text-white text-xl font-bold px-8 py-5 rounded-full shadow-xl transition-all duration-300 text-center"
            >
              Conhecer o Guia Completo →
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-6 text-center text-sm">
        <p>© {new Date().getFullYear()} Guia Anti-Golpe. Todos os direitos reservados.</p>
      </footer>
    </div>
  )
}

export default DownloadPage
